/**
 * The asset PDF exports. The server answers an inline PDF; the browser shows it in a new tab. The tab is opened
 * before the request (a popup opened after an await is blocked), then pointed at the PDF, or closed when the server
 * refuses, so a refusal (429: ten exports a minute) reaches the person as a toast instead of an error page.
 */

import { useMutation } from '@tanstack/react-query';

import { ApiError } from '../client';

export type ExportAudience = 'external' | 'internal';

export function exportUrl(assetId: string, audience: ExportAudience): string {
  return `/api/assets/${assetId}/export.pdf?audience=${audience}`;
}

async function refusal(response: Response): Promise<ApiError> {
  if (response.status === 401) window.dispatchEvent(new CustomEvent('wynxx:unauthorized'));
  if (response.status === 429) return new ApiError(429, 'Too many exports; try again in a minute.');
  const payload = (await response.json().catch(() => null)) as { detail?: unknown } | null;
  return new ApiError(response.status, typeof payload?.detail === 'string' ? payload.detail : `Request failed (${response.status}).`);
}

async function openPdf(path: string): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const response = await fetch(path, {
      credentials: 'same-origin',
      headers: { Accept: 'application/pdf', 'X-Requested-With': 'wynxx-discover' },
    });
    if (!response.ok) throw await refusal(response);
    const url = URL.createObjectURL(await response.blob());
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener');
    }
    // Let the tab load the PDF before the URL goes.
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    tab?.close();
    throw error;
  }
}

export function useExportPdf(assetId: string) {
  return useMutation({
    meta: { failure: 'Could not export the PDF' },
    mutationFn: (audience: ExportAudience) => openPdf(exportUrl(assetId, audience)),
  });
}
